import { Navigate, Outlet } from "react-router-dom";

import { useMyPermessi } from "@/hooks/usePermessi";
import { hasPermesso, hasPermessoSezione } from "@/lib/permessi";
import type { RisorsaTipo } from "@/types/api";

interface PermessoGuardProps {
  /** Tipo+key del permesso richiesto per accedere alla route. */
  tipo: RisorsaTipo;
  permessoKey: string;
  /**
   * Per le **sezioni di modulo**: il modulo da cui la sezione eredita quando non
   * ha un grant esplicito (es. `permessoKey="m3.partenze"` + `ereditaDa="M3"`).
   * Senza questo, una sezione nuova sarebbe negata per default a tutti i ruoli
   * configurati — cioè toglierebbe accesso il giorno del deploy.
   */
  ereditaDa?: string;
  /** Path di redirect quando il permesso non è concesso. Default: `/` (Panoramica). */
  redirectTo?: string;
}

/**
 * Guard di route basato sui permessi granulari.
 *
 * Esempio in router.tsx:
 *   {
 *     element: <PermessoGuard tipo="nav_voce" permessoKey="documenti" />,
 *     children: [{ path: "documenti", element: <DocumentiPage /> }]
 *   }
 *
 * Maitime bypassa sempre (gestito da `hasPermesso`). Mentre permessi sono in
 * caricamento, non redirezziamo (evita flash): mostriamo Outlet così la pagina
 * monta normalmente e il backend rifiuterà con 403 se serve (defense-in-depth).
 */
export function PermessoGuard({
  tipo,
  permessoKey,
  ereditaDa,
  redirectTo = "/",
}: PermessoGuardProps) {
  const { data: perms, isLoading } = useMyPermessi();

  if (isLoading) {
    return <Outlet />;
  }
  const concesso = ereditaDa
    ? hasPermessoSezione(perms, permessoKey, ereditaDa)
    : hasPermesso(perms, tipo, permessoKey);
  if (!concesso) {
    return <Navigate to={redirectTo} replace />;
  }
  return <Outlet />;
}
